import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, RefreshCw, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePremiumDialog } from '@/contexts/PremiumDialogContext';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface Emote {
  id: number;
  type: '💖' | '👍' | '👻' | '✨' | '🌟' | '🎉' | '🤩';
  x: number;
  y: number;
  scale: number;
  rotation: number;
  opacity: number;
  delay: number;
  duration: number;
}
interface Lightning {
  id: number;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  delay: number;
  duration: number;
}

const Premium = () => {
  const navigate = useNavigate();
  const {
    handleSubscribe,
    isProcessing,
    verifySubscriptionStatus,
    isVerifying,
    handleManageSubscription,
    subscriptionRenewalDate,
    subscriptionStatus
  } = usePremiumDialog();
  const [emotes, setEmotes] = useState<Emote[]>([]);
  const [lightnings, setLightnings] = useState<Lightning[]>([]);
  const [isPremium, setIsPremium] = useState<boolean | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  
  useEffect(() => {
    const checkPremiumStatus = async () => {
      setIsLoadingStatus(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setIsPremium(false);
          return;
        }
        
        const lastCheckoutSessionId = sessionStorage.getItem('lastCheckoutSessionId');
        
        if (lastCheckoutSessionId) {
          console.log('Found lastCheckoutSessionId in sessionStorage:', lastCheckoutSessionId);
          
          const wasUpdated = await verifySubscriptionStatus({ checkoutSessionId: lastCheckoutSessionId });
          
          if (wasUpdated) {
            console.log('Subscription was updated via lastCheckoutSessionId');
            setIsPremium(true);
            sessionStorage.removeItem('lastCheckoutSessionId');
          }
        }
        
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('is_premium, email, stripe_customer_id')
          .eq('id', session.user.id)
          .single();
          
        if (error) {
          console.error('Error fetching profile:', error);
          setIsPremium(false);
        } else {
          setIsPremium(profile?.is_premium || false);
          
          if (profile && profile.stripe_customer_id && !profile.is_premium) {
            console.log('User has Stripe customer ID but no premium status, verifying...');
            const wasUpdated = await verifySubscriptionStatus({ force: true });
            if (wasUpdated) {
              setIsPremium(true);
            }
          }
        }
      } catch (error) {
        console.error('Error checking premium status:', error);
        setIsPremium(false);
      } finally {
        setIsLoadingStatus(false);
      }
    };
    
    checkPremiumStatus();
  }, [verifySubscriptionStatus]);
  
  const handleVerifySubscription = async () => {
    try {
      const wasUpdated = await verifySubscriptionStatus({ force: true });
      if (wasUpdated) {
        setIsPremium(true);
        toast({
          title: "Subscription Verified",
          description: "Your premium status has been updated!",
        });
      } else {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('is_premium')
            .eq('id', session.user.id)
            .single();
            
          if (profile) {
            setIsPremium(profile.is_premium || false);
          }
        }
        
        toast({
          title: "Subscription Check Complete",
          description: isPremium 
            ? "You already have premium access." 
            : "No active subscription found.",
        });
      }
    } catch (error) {
      console.error('Error verifying subscription:', error);
      toast({
        title: "Verification Failed",
        description: "Could not verify your subscription status.",
        variant: "destructive",
      });
    }
  };
  
  const handleVerifyByEmail = async (email: string) => {
    if (!email) return;
    
    try {
      const wasUpdated = await verifySubscriptionStatus({ email, force: true });
      toast({
        title: wasUpdated ? "Subscription Updated" : "Verification Complete",
        description: wasUpdated 
          ? `Premium status updated for ${email}` 
          : `No changes needed for ${email}`,
      });
    } catch (error) {
      console.error('Error verifying by email:', error);
      toast({
        title: "Verification Failed",
        description: `Could not verify subscription for ${email}`,
        variant: "destructive",
      });
    }
  };
  
  useEffect(() => {
    const emoteTypes: Emote['type'][] = ['💖', '👍', '👻', '✨', '🌟', '🎉', '🤩'];
    const newEmotes: Emote[] = [];
    for (let i = 0; i < 20; i++) {
      newEmotes.push({
        id: i,
        type: emoteTypes[Math.floor(Math.random() * emoteTypes.length)],
        x: Math.random() * 100,
        y: Math.random() * 100,
        scale: Math.random() * 0.5 + 0.5,
        rotation: Math.random() * 360,
        opacity: Math.random() * 0.5 + 0.5,
        delay: Math.random() * 5,
        duration: Math.random() * 15 + 15
      });
    }
    setEmotes(newEmotes);

    const newLightnings: Lightning[] = [];
    for (let i = 0; i < 8; i++) {
      newLightnings.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 60,
        rotation: Math.random() * 45 - 22.5,
        scale: Math.random() * 0.5 + 0.7,
        delay: Math.random() * 10,
        duration: 0.3
      });
    }
    setLightnings(newLightnings);

    const interval = setInterval(() => {
      setLightnings(prev => {
        const newLightnings = [...prev];
        return newLightnings.map(lightning => ({
          ...lightning,
          x: Math.random() * 100,
          y: Math.random() * 60,
          rotation: Math.random() * 45 - 22.5,
          scale: Math.random() * 0.5 + 0.7,
          delay: Math.random() * 2
        }));
      });
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const lightningPath = "M13,0 L0,11 L7,13 L0,24 L18,13 L11,11 Z";

  const formatDate = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  };

  return <div className="min-h-screen bg-black text-white pt-20 px-4 relative overflow-hidden">
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        {emotes.map(emote => <motion.div key={emote.id} initial={{
        x: `${emote.x}vw`,
        y: '100vh',
        scale: emote.scale,
        rotate: emote.rotation,
        opacity: 0
      }} animate={{
        y: '-20vh',
        opacity: [0, emote.opacity, 0],
        rotate: [emote.rotation, emote.rotation + 180]
      }} transition={{
        duration: emote.duration,
        delay: emote.delay,
        repeat: Infinity,
        ease: "linear"
      }} className="absolute text-4xl">
            {emote.type}
          </motion.div>)}
        
        {lightnings.map(lightning => <motion.div key={lightning.id} className="absolute" style={{
        left: `${lightning.x}vw`,
        top: `${lightning.y}vh`
      }} initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: [0, 1, 0.8, 0],
        scale: [0, lightning.scale, lightning.scale * 0.9, 0]
      }} transition={{
        duration: lightning.duration,
        delay: lightning.delay,
        repeat: Infinity,
        repeatDelay: Math.random() * 10 + 5,
        ease: "easeOut"
      }}>
            <svg width="40" height="60" viewBox="0 0 18 24" style={{
          transform: `rotate(${lightning.rotation}deg)`,
          filter: 'drop-shadow(0 0 8px rgba(229, 222, 255, 0.8))'
        }}>
              <path d={lightningPath} fill="#E5DEFF" />
            </svg>
            
            <motion.div className="absolute inset-0 bg-primary/20 rounded-full blur-xl" initial={{
          scale: 1,
          opacity: 0
        }} animate={{
          scale: [1, 10, 1],
          opacity: [0, 0.3, 0]
        }} transition={{
          duration: 0.5,
          delay: lightning.delay,
          repeat: Infinity,
          repeatDelay: Math.random() * 10 + 5,
          ease: "easeOut"
        }} style={{
          width: '40px',
          height: '60px',
          top: '-20px',
          left: '-20px'
        }} />
          </motion.div>)}
      </div>
      
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-primary/20 via-primary/10 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-primary/20 via-primary/10 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80 mix-blend-overlay"></div>
      </div>

      <Button variant="ghost" size="icon" className="absolute top-20 left-4 text-white z-10" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-6 w-6" />
      </Button>

      <div className="max-w-3xl mx-auto space-y-12 pt-12 relative z-10">
        <motion.h1 className="text-5xl font-bold text-[#E5DEFF] text-center" animate={{
        textShadow: ['0 0 10px rgba(229, 222, 255, 0.3)', '0 0 20px rgba(229, 222, 255, 0.5)', '0 0 10px rgba(229, 222, 255, 0.3)']
      }} transition={{
        duration: 3,
        repeat: Infinity,
        repeatType: 'reverse'
      }}>
          Super Hyper Remix ✨
        </motion.h1>

        <div className="text-center space-y-6">
          <p className="text-gray-300 text-xl max-w-2xl mx-auto">
            Level up your profile with exclusive premium features
          </p>
          
          {isLoadingStatus ? (
            <div className="flex justify-center items-center h-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : isPremium ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-[#E5DEFF]/10 to-primary/10 p-6 rounded-xl border border-primary/20"
            >
              <h3 className="text-2xl font-bold text-primary flex items-center justify-center gap-2">
                <Sparkles className="h-6 w-6" />
                Premium Active
                <Sparkles className="h-6 w-6" />
              </h3>
              <p className="text-gray-300 mt-2">You already have access to all premium features!</p>
              {subscriptionRenewalDate && (
                <div className="mt-4 flex flex-col items-center text-sm text-primary-foreground bg-primary/10 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Next Renewal:</span>
                    <span>{formatDate(subscriptionRenewalDate)}</span>
                  </div>
                  {subscriptionStatus === "canceled" || subscriptionStatus === "cancelling" ? (
                    <div className="flex items-center gap-2 text-red-400 mt-1">
                      <span>Subscription cancelled.</span>
                      <span>Access ends on {formatDate(subscriptionRenewalDate)}</span>
                    </div>
                  ) : null}
                </div>
              )}
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  onClick={handleManageSubscription}
                  disabled={isProcessing}
                  variant="default"
                  className="bg-primary text-primary-foreground"
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  {isProcessing ? "Processing..." : "Manage Subscription"}
                </Button>
                
                <Button
                  onClick={handleVerifySubscription}
                  disabled={isVerifying}
                  variant="outline"
                  className="border-primary/40 text-primary"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${isVerifying ? 'animate-spin' : ''}`} />
                  {isVerifying ? "Verifying..." : "Verify Subscription"}
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="mt-8 space-y-4">
              <Button onClick={handleSubscribe} disabled={isProcessing} size="lg" className={cn("bg-gradient-to-r from-[#E5DEFF] to-primary text-primary-foreground text-lg px-8 py-6 h-auto font-semibold group", "shadow-[0_0_15px_rgba(124,58,237,0.5)] hover:shadow-[0_0_20px_rgba(124,58,237,0.7)]")}>
                <Sparkles className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                {isProcessing ? "Processing..." : "Subscribe Now"}
              </Button>
              
              <div>
                <Button 
                  onClick={handleVerifySubscription} 
                  disabled={isVerifying}
                  variant="outline" 
                  className="text-sm text-gray-400 border-gray-800"
                >
                  <RefreshCw className={`mr-2 h-3 w-3 ${isVerifying ? 'animate-spin' : ''}`} />
                  {isVerifying ? "Checking..." : "Already subscribed? Check status"}
                </Button>
              </div>
            </motion.div>
          )}
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 mt-12">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-[#1A1F2C] p-6 rounded-xl border border-primary/20">
            <div className="text-2xl mb-4">💠</div>
            <h3 className="text-xl font-semibold text-primary mb-2">Holographic Content</h3>
            <p className="text-gray-400">Your bulletins are holographic and interactable!</p>
          </motion.div>
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-[#1A1F2C] p-6 rounded-xl border border-primary/20">
            <div className="text-2xl mb-4">💥</div>
            <h3 className="text-xl font-semibold text-primary mb-2">Avatar Stroke</h3>
            <p className="text-gray-400">A bold aura around your avatar that demands attention wherever you post.</p>
          </motion.div>
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="bg-[#1A1F2C] p-6 rounded-xl border border-primary/20">
            <div className="text-2xl mb-4">⚡</div>
            <h3 className="text-xl font-semibold text-primary mb-2">Post Borders</h3>
            <p className="text-gray-400">Frame your content with legendary glow effects that make your posts pop.</p>
          </motion.div>
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="bg-[#1A1F2C] p-6 rounded-xl border border-primary/20">
            <div className="text-2xl mb-4">🎞</div>
            <h3 className="text-xl font-semibold text-primary mb-2">GIF & MP4 Posting</h3>
            <p className="text-gray-400">Bring your posts to life with animated GIFs and video content.</p>
          </motion.div>
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} className="bg-[#1A1F2C] p-6 rounded-xl border border-primary/20">
            <div className="text-2xl mb-4">🌈</div>
            <h3 className="text-xl font-semibold text-primary mb-2">Unique Username Color</h3>
            <p className="text-gray-400">Receive a unique colored username that helps you stand out instantly in comments and posts.</p>
          </motion.div>
          
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} className="bg-[#1A1F2C] p-6 rounded-xl border border-primary/20">
            <div className="text-2xl mb-4">🛠</div>
            <h3 className="text-xl font-semibold text-primary mb-2">More Coming Soon</h3>
            <p className="text-gray-400">We're constantly adding new premium features - subscribers get them all automatically!</p>
          </motion.div>
        </div>
      </div>
    </div>;
};

export default Premium;
